import { inject, Injectable } from '@angular/core';
import { Observable, ReplaySubject, tap, of, switchMap, map } from 'rxjs';
import { NavigationItem } from './navigation.types';
import { navigations as _navItems } from './navigation.data';
import { TranslocoService } from '@jsverse/transloco';
import { AuthAppService } from '@modules/auth';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private readonly _navigation: ReplaySubject<NavigationItem[]> =
        new ReplaySubject<NavigationItem[]>(1);

    private readonly authService = inject(AuthAppService);
    private readonly translocoService = inject(TranslocoService);

    constructor() {
        // Subscribe once — root service lives for the lifetime of the app.
        // Repeated calls to get() previously re-subscribed each time, leaking subscriptions.
        this.translocoService.langChanges$.subscribe(() => {
            setTimeout(() => {
                this._navigation.next(this.translateNavigationItems(_navItems));
            }, 200);
        });
    }



    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<NavigationItem[]> {
        return this._navigation.asObservable().pipe(
            switchMap((navigationItems) => {
                return this.authService.user$.pipe(
                    map((user) => {
                        if (!user?.rights) {
                            return [] as NavigationItem[];
                        }
                        // Filter navigation items based on user claims
                        return this.filterNavigationItemsByAuth(navigationItems, user.rights || []); // Assuming rights is an array of claims
                    })
                );
            })
        );
    }

    // Helper method to recursively filter navigation items
    private filterNavigationItemsByAuth(items: NavigationItem[], userClaims: string[]): NavigationItem[] {
        return items
            .filter(item =>
                !item.authorized?.length ||
                item.authorized.some(claim => userClaims.includes(claim))
            )
            .map(item => {
                // Shallow-copy each item so we never mutate the original nav data objects
                if (item.children) {
                    return { ...item, children: this.filterNavigationItemsByAuth(item.children, userClaims) };
                }
                return item;
            });
    }

    // Helper method to recursively translate navigation items
    private translateNavigationItems(items: NavigationItem[]): NavigationItem[] {
        return items.map(item => {
            // Create a copy of the item to avoid mutating the original
            const translatedItem = { ...item };

            // Translate the title if it exists
            if (translatedItem.title) {
                // Use the item's id as the translation key, with fallback to original title
                const translationKey = `navigation.${translatedItem.id}`;
                translatedItem.title = this.translocoService.translate(translationKey) || translatedItem.title;
            }

            // Translate subtitle if it exists
            if (translatedItem.subtitle) {
                const translationKey = `navigation.${translatedItem.id}.subtitle`;
                translatedItem.subtitle = this.translocoService.translate(translationKey) || translatedItem.subtitle;
            }

            // Recursively translate children
            if (translatedItem.children) {
                translatedItem.children = this.translateNavigationItems(translatedItem.children);
            }

            return translatedItem;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<NavigationItem[]> {
        return of(this.translateNavigationItems(_navItems)).pipe(
            tap((navigation) => this._navigation.next(navigation))
        );
    }
}
